import { Moment } from 'moment';
import { Status } from './ExamModuleTypes';
import {
	Answers,
	ApplicationSummary,
	Functionary,
	Preceptor,
	PredefinedReason,
	UserRole,
} from './VolunteerApplicationServiceTypes';

export type Gender = 'NOT_PROVIDED' | 'MALE' | 'FEMALE';

export interface PagingConfig {
	pageNo?: number;
	pageSize?: number;
}

export interface Pagination extends PagingConfig {
	totalNoOfRecords: number;
	totalNoOfPages?: number;
}

export interface CandidateLanguage {
	id: number;
	name: string;
}

export type SortingOrder =
	| 'SORTING_ORDER_UNSPECIFIED'
	| 'ASCENDING'
	| 'DESCENDING';

export type NPHHSortingFieldName =
	| 'SORTING_FIELD_NAME_UNSPECIFIED'
	| 'PRECEPTOR_ID'
	| 'NAME'
	| 'SRCM_ID'
	| 'BATCH'
	| 'ADDRESS'
	| 'POSTAL_CODE'
	| 'COUNTRY'
	| 'CITY'
	| 'STATE'
	| 'ZONE'
	| 'MOBILE'
	| 'EMAIL'
	| 'GENDER'
	| 'GROUP_ID'
	| 'GROUP_NUMBER';

export interface NPHHSortingRequest {
	sortingFieldName: NPHHSortingFieldName;
	sortingOrder: SortingOrder;
}

export interface Candidate {
	id: number;
	key?: number;
	firstName: string;
	middleName: string;
	lastName: string;
	srcmId: string;
	email: string;
	email2: string;
	mobile: string;
	mobile2: string;
	phone: string;
	phone2: string;
	age: number;
	gender: Gender;
	dateOfBirth: string;
	dateOfJoining: string;
	addressLine1: string;
	addressLine2: string;
	addressLine3: string;
	addressLine4: string;
	cityId: number;
	city: string;
	stateId: number;
	state: string;
	countryId: number;
	country: string;
	postalCode: string;
	centerId: number;
	center: string;
	mySrcmPartnerId: number;
	prefectId: number;
	prefectStatus: string;
	prefectStartDate: string;
	respPrefectId: number;
	respPrefectName: string;
	batch: string;
	zone: string;
	locality: string;
	languages: Array<CandidateLanguage>;
	preceptorMappedToGroup: boolean;
	preceptorAssignedToMentor: boolean;
	pincode?: string;
	photoUrl?: string;
	monthOfJoining?: string;
	educationQualification?: string;
	firebaseId: string;
}
export interface PreworkCandidateSummary {
	applicationId: number;
	applicant: User;
	batchId: number;
	batchName: string;
	centerId?: number;
	center: Center;
	zone?: string;
	mentorId?: string;
	mentorName?: string;
	respPrefectId?: number;
	respPrefectName?: string;
	prefectStatus?: string;
	prefectStartDate?: string;
	candidateMappedToGroup?: boolean;
	candidateAssignedToMentor?: boolean;
	preferredLanguageOfTraining?: string;
}

export interface User {
	photoUrl?: string;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	addressLine1?: string;
	addressLine2?: string;
	addressLine3?: string;
	addressLine4?: string;
	city?: string;
	mobile?: string;
	email?: string;
	pincode?: string;
	firebaseId: string;
	srcmid: string;
	srcmId: string;
	gender?: string;
	age?: number;
	monthOfJoining?: string;
	educationQualification?: string;
	profession?: string;
	professionDetails?: string;
	dateOfBirth?: string;
	mySrcmPartnerId?: number;
	state?: string;
	key?: number;
	id?: number;
	languages: Array<CandidateLanguage>;
}

export interface PreworkGroup {
	id: number;
	preworkGroupNumber: string;
	preworkGroupName: string;
	noOfMembers: number;
	mentor: Candidate;
	preworkGroupMembers: Array<PreworkCandidateSummary>;
	batch: Batch;
}

export interface Country {
	value: number | string;
	label: string;
}
export interface State {
	id?: number;
	name?: string;
}

export interface CandidateBatch {
	id: number;
	key?: number;
	batchName: string;
	batchCode: string;
	description: string;
	startDate: string;
	endDate: string;
}

export interface GetBatchesResponse {
	batches: Array<Batch>;
	pagination: Pagination;
}

export interface PreworkDashboardFilter {
	candidateMappedToGroup: boolean;
	candidateAssignedToMentor: boolean;
	candidateNotMappedToGroup: boolean;
	candidateNotAssignedToMentor: boolean;
}

export interface PreworkCandidateFilter {
	srcmId?: string;
	name?: string;
	email?: string;
	mobile?: string;
	countryNames?: Array<string>;
	stateNames?: Array<string>;
	cityNames?: Array<string>;
	centerNames?: Array<string>;
	postalCode?: string;
	// eslint-disable-next-line
	zoneNames?: string | Country;
	batch?: string;
	gender?: Gender;
	prefectStatus?: Array<string>;
	prefectStartDate?: string;
	language?: string;
	respPrefectName?: string;
	showNewCandidatesForGrouping?: boolean;
	showAllCandidates?: boolean;
	countryIds?: Array<number>;
	stateIds?: Array<number>;
	cityIds?: Array<number>;
	centerIds?: Array<number>;
	zoneIds?: Array<number>;
	sortingRequests?: Array<NPHHSortingRequest>;
	showPreworkDashboardDetail?: boolean;
	preworkDashboardFilterEnabled?: boolean;
	preworkDashboardFilter?: PreworkDashboardFilter;
	regionIds?: Array<number>;
	batchId?: number;
	mentorName?: string;
	showMentors?: boolean;
}

export interface GetZonesRequest {
	functionarySrcmId: string;
	batchId?: number;
	regionIds?: Array<number>;
	selectedRoleCode: UserRole;
}

export interface PreworkGroupFilter {
	preworkGroupNumber?: string;
	noOfMembers?: number;
	mentorId?: number;
	sortingRequests?: Array<NPHHSortingRequest>;
	regionIds?: Array<number>;
	zoneIds?: Array<number>;
	zoneNames?: Array<string>;
	batchId?: number;
}

export interface BatchFilter {
	batchId?: number;
	batchName?: string;
	batchCode?: string;
	batchDescription?: string;
	startDate?: string | Moment;
	endDate?: string | Moment;
	isPreworkEnabled?: boolean;
	sortingOrder?: string;
	status?: Status;
}

export interface Batch {
	id?: number;
	name: string;
	description: string;
	startDate?: string;
	endDate?: string;
	snpResultDateForApprover?: string;
	snpResultDateForProposer?: string;
	snpResultDateForZC?: string;
	snpResultDateForCandidate?: string;
	status?: Status; // getting the response as string why we need to
}

export interface GetCandidatesRequest {
	functionarySrcmId: string;
	pageIndex?: number;
	pageSize?: number;
	candidateFilter: PreworkCandidateFilter;
	selectedRoleCode: UserRole;
}

export interface BooleanValue {
	value: boolean;
}

export interface Center {
	id: number;
	name: string;
	externalId?: string;
	zone: string;
	centerType: string;
}

export interface Region {
	regionId: number;
	regionName: string;
}

export interface GetCandidatesResponse {
	candidates: Array<PreworkCandidateSummary>;
	pagination: Pagination;
}

export interface PreworkDashboardDetail {
	totalCandidates: number;
	totalCandidatesNotMappedWithGroup: number;
	totalCandidatesMappedWithGroup: number;
	totalCandidatesNotAssignedWithMentor: number;
	totalCandidatesAssignedWithMentor: number;
}

export interface FunctionaryInfo {
	id?: number;
	printName?: string;
	email?: string;
	mobile?: string;
	gender?: string;
	srcmId: string;
	firebaseId?: string;
	isApprover?: boolean;
	isCareTeam?: boolean;
	isZC?: boolean;
	adminSrcmId?: string;
	zone?: Array<{ value: string }>;
	functionaryType?: string;
	homeZone?: string;
	homeZoneToDisplay?: { value: string | undefined };
}

export interface FunctionaryOfZone {
	functionary: FunctionaryInfo;
	zone?: Array<string>;
	approverZone?: Array<string>;
	zcZone?: Array<string>;
}

export interface GetFunctionaryRequest {
	functionarySrcmId?: string;
}

export interface CreatePreworkGroupRequest {
	functionarySrcmId: string;
	candidateIds: Array<number>;
	batchId?: number;
}

export interface CreatePreworkGroupResponse {
	preworkGroup: PreworkGroup;
}

export interface UpdatePreworkGroupRequest extends CreatePreworkGroupRequest {
	preworkGroupId: number;
}

export interface UpdatePreworkGroupResponse {
	preworkGroup: PreworkGroup;
}

export interface GetPreworkGroupRequest {
	functionarySrcmId: string;
	pageIndex: number;
	pageSize: number;
	preworkGroupFilter: PreworkGroupFilter;
	selectedRoleCode: UserRole;
}

export interface GetPreworkGroupResponse {
	preworkGroup: Array<PreworkGroup>;
	pagination: Pagination;
}

export interface AssignPreworkMentorRequest {
	functionarySrcmId: string;
	preworkGroupId: number;
	mentorId: number;
}

export interface CandidateGroupRequest {
	functionarySrcmId: string;
	id: number;
}

export interface GetRegionsResponse {
	regions: Array<Region>;
}

export interface RegionWisePreworkGraphicalView {
	regionId: number;
	region: string;
	candidateMappedWithGroupCount: number;
	candidateNotMappedWithGroupCount: number;
	candidateAssignedWithMentorCount: number;
	candidateNotAssignedWithMentorCount: number;
}
export interface ZoneWisePreworkGraphicalView {
	zoneId: number;
	zone: string;
	candidateMappedWithGroupCount: number;
	candidateNotMappedWithGroupCount: number;
	candidateAssignedWithMentorCount: number;
	candidateNotAssignedWithMentorCount: number;
}

export interface PreworkGraphicalViewResponse {
	preworkDashboardDetail: PreworkDashboardDetail;
	regionWisePreworkGraphicalView?: Array<RegionWisePreworkGraphicalView>;
	zoneWisePreworkGraphicalView?: Array<ZoneWisePreworkGraphicalView>;
}

export interface GetBatchesRequest {
	functionarySrcmId: string;
	pageIndex: number;
	pageSize: number;
	batchFilter: BatchFilter;
}

export interface GetRegionsRequest {
	functionarySrcmId?: string;
	batchId: number;
	selectedRoleCode: UserRole;
}

export interface GetPreworkCandidatesRequest {
	functionarySrcmId: string;
	selectedRoleCode: UserRole;
	pageIndex?: number;
	pageSize?: number;
	candidateFilter?: PreworkCandidateFilter;
}

export interface GetPreworkCandidatesResponse {
	candidates: Array<PreworkCandidateSummary>;
	pagination: Pagination;
}

export enum PreworkCompletionStatus {
	NOT_STARTED = 'NOT_STARTED',
	INCOMPLETE = 'INCOMPLETE',
	PARTIALLY_COMPLETED = 'PARTIALLY_COMPLETED',
	COMPLETED = 'COMPLETED',
}

export enum PreworkReviewAction {
	NOT_APPLICABLE_REVIEW_PREWORK = 'NOT_APPLICABLE_REVIEW_PREWORK',
	PREWORK_APPROVE = 'PREWORK_APPROVE',
	PREWORK_REJECT = 'PREWORK_REJECT',
	PENDING = 'PENDING',
	EXTEND_TO_PREWORK_MENTOR = 'EXTEND_TO_PREWORK_MENTOR',
}

export enum PreworkStatus {
	PREWORK_PENDING = 'PREWORK_PENDING',
	PREWORK_APPROVED = 'PREWORK_APPROVED',
	PREWORK_REJECTED = 'PREWORK_REJECTED',
}

export interface PreworkApplicationFilters {
	zoneNames?: Array<string>;
	applicantName?: string;
	gender?: string;
	ProposerName?: string;
	applicantSrcmId?: string;
	ProposerSrcmId?: string;
	preworkStatuses?: Array<PreworkCompletionStatus>;
	preworkRecommendation?: Array<PreworkReviewAction>;
}

export interface PreworkApplicationsRequest {
	batchId: number;
	functionarySrcmId: string;
	pageIndex?: number;
	pageSize?: number;
	filters?: PreworkApplicationFilters;
	selectedRoleCode: UserRole;
}

export interface ApplicationPreworkStatus {
	applicationId: number;
	preworkAnswers: Answers;
	preworkCompletionStatus: PreworkCompletionStatus;
	preworkMentorAnswers: Answers;
	preworkMentorCompletionStatus: PreworkCompletionStatus;
	preworkMentorIncompleteAttempts: number;
	isForwardedToApprover: boolean;
	preworkMentorNextAttemptDate: string;
	application: ApplicationSummary;
	isIncompleteMarkingBlocked?: boolean;
	candidateSubmissionDate?: string | Moment;
	preworkMentorSubmissionDate?: string | Moment;
	approver?: Functionary;
	approverComment?: string;
	approverPreworkRecommendation?: PreworkReviewAction;
	canReapplyDate?: string;
	daysToEnableReApply?: string;
	preworkMentor?: Preceptor;
	preworkStatus?: PreworkStatus;
}

export interface PreworkApplicationsResponse {
	preworkStatus: Array<ApplicationPreworkStatus>;
	pagination: Pagination;
}

export interface PreworkMentorAnswersRequest {
	batchId: number;
	applicationId: number;
	mentorPreworkAnswers: Answers;
	mentorCompletionStatus: PreworkCompletionStatus;
	isForwardedToApprover?: boolean;
}

export interface PreworkFeedback {
	preworkRecommendation?: PreworkReviewAction;
	comments?: string;
	predefinedReasons?: Array<PredefinedReason>;
}

export interface PreworkRecommendationRequest {
	batchId: number;
	applicationId: number;
	approverPreworkFeedback: PreworkFeedback;
	functionarySrcmId?: string;
	selectedRoleCode: UserRole;
}

export interface PreworkStatusFunctionaryPredefinedComment {
	id: number;
	predefinedReason: PredefinedReason;
	providedBy?: string;
	functionarySrcmId?: string;
	createdDate?: string;
	modifiedDate?: string;
}
